import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Divider
} from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';
import { format, startOfMonth, endOfMonth, isSameDay, isWithinInterval } from 'date-fns';
import { Badge } from '@mui/material';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';
import { useAuth } from '../../contexts/AuthContext';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const InterventionsTab = () => {
  const { userSchool } = useAuth();
  const [interventions, setInterventions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('day'); // 'day', 'month'

  useEffect(() => {
    fetchInterventions();
  }, [userSchool]);

  const fetchInterventions = async () => {
    try {
      setLoading(true);

      // Récupérer toutes les interventions
      const q = query(collection(db, COLLECTIONS.INTERVENTIONS));
      const querySnapshot = await getDocs(q);
      
      // Enrichir les interventions avec les informations du demandeur
      const interventionsData = await Promise.all(
        querySnapshot.docs.map(async (docSnapshot) => {
          const data = docSnapshot.data();
          const enrichedData = {
            id: docSnapshot.id,
            ...data,
            date: data.date?.toDate() || null
          };

          // Récupérer le nom du demandeur depuis la demande de travail associée
          if (data.workRequestId) {
            try {
              const workRequestDoc = await getDoc(doc(db, COLLECTIONS.WORK_REQUESTS, data.workRequestId));
              if (workRequestDoc.exists()) {
                const workRequestData = workRequestDoc.data();
                
                // Récupérer le nom du demandeur
                if (workRequestData.requestingServiceId || workRequestData.createdBy) {
                  const requesterId = workRequestData.requestingServiceId || workRequestData.createdBy;
                  
                  // Essayer d'abord dans la collection SERVICES
                  let requesterDoc = await getDoc(doc(db, COLLECTIONS.SERVICES, requesterId));
                  
                  if (requesterDoc.exists()) {
                    const requesterData = requesterDoc.data();
                    enrichedData.requesterName = requesterData.name || requesterData.displayName || 'Service';
                  } else {
                    // Si non trouvé, essayer dans la collection users
                    const userDoc = await getDoc(doc(db, 'users', requesterId));
                    if (userDoc.exists()) {
                      const userData = userDoc.data();
                      enrichedData.requesterName = userData.displayName || userData.name || userData.email || 'Utilisateur';
                    }
                  }
                }
              }
            } catch (error) {
              console.error('Erreur récupération demandeur pour intervention:', error);
            }
          }

          return enrichedData;
        })
      );

      setInterventions(interventionsData);
    } catch (error) {
      console.error('Erreur lors de la récupération des interventions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredInterventions = () => {
    if (viewMode === 'day') {
      return interventions.filter(intervention =>
        intervention.date && isSameDay(intervention.date, selectedDate)
      );
    } else if (viewMode === 'month') {
      const monthStart = startOfMonth(selectedDate);
      const monthEnd = endOfMonth(selectedDate);
      return interventions.filter(intervention =>
        intervention.date && isWithinInterval(intervention.date, { start: monthStart, end: monthEnd })
      ).sort((a, b) => a.date - b.date);
    }
    return [];
  };

  const filteredInterventions = getFilteredInterventions();

  // Statistiques
  const stats = {
    total: interventions.length,
    today: interventions.filter(i => i.date && isSameDay(i.date, new Date())).length,
    thisMonth: interventions.filter(i => {
      if (!i.date) return false;
      const monthStart = startOfMonth(new Date());
      const monthEnd = endOfMonth(new Date());
      return isWithinInterval(i.date, { start: monthStart, end: monthEnd });
    }).length,
    planned: interventions.filter(i => i.status === 'planned').length
  };

  // Fonction pour vérifier si une date a des interventions
  const hasInterventions = (date) => {
    return interventions.some(intervention => 
      intervention.date && isSameDay(intervention.date, date)
    );
  };

  // Fonction pour compter les interventions d'une date
  const countInterventions = (date) => {
    return interventions.filter(intervention => 
      intervention.date && isSameDay(intervention.date, date)
    ).length;
  };

  // Composant personnalisé pour les jours du calendrier
  const CustomDay = (props) => {
    const { day, outsideCurrentMonth, ...other } = props;
    const interventionCount = countInterventions(day);
    const hasIntervention = interventionCount > 0;

    return (
      <Badge
        key={day.toString()}
        overlap="circular"
        badgeContent={hasIntervention && !outsideCurrentMonth ? interventionCount : undefined}
        color="primary"
      >
        <PickersDay
          {...other}
          day={day}
          outsideCurrentMonth={outsideCurrentMonth}
          sx={{
            ...(hasIntervention && !outsideCurrentMonth && {
              backgroundColor: 'primary.light',
              color: 'primary.contrastText',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: 'primary.main',
              },
              '&.Mui-selected': {
                backgroundColor: 'primary.dark',
              }
            })
          }}
        />
      </Badge>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* KPIs */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total interventions
              </Typography>
              <Typography variant="h4">{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Aujourd'hui
              </Typography>
              <Typography variant="h4" color="primary">{stats.today}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Ce mois
              </Typography>
              <Typography variant="h4">{stats.thisMonth}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Planifiées
              </Typography>
              <Typography variant="h4" color="warning.main">{stats.planned}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Calendrier */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Calendrier des interventions
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
              <DateCalendar
                value={selectedDate}
                onChange={(newDate) => setSelectedDate(newDate)}
                slots={{
                  day: CustomDay,
                }}
                sx={{ width: '100%' }}
              />
            </LocalizationProvider>
            
            {/* Légende */}
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                Légende :
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ 
                    width: 24, 
                    height: 24, 
                    bgcolor: 'primary.light', 
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Typography variant="caption" sx={{ color: 'primary.contrastText', fontWeight: 'bold' }}>
                      1
                    </Typography>
                  </Box>
                  <Typography variant="caption">Jour avec intervention(s)</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Badge badgeContent={2} color="primary">
                    <Box sx={{ width: 24, height: 24 }} />
                  </Badge>
                  <Typography variant="caption">Nombre d'interventions</Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Liste des interventions */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                {viewMode === 'day' && `Interventions du ${format(selectedDate, 'dd MMMM yyyy', { locale: fr })}`}
                {viewMode === 'month' && `Interventions de ${format(selectedDate, 'MMMM yyyy', { locale: fr })}`}
              </Typography>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newValue) => newValue && setViewMode(newValue)}
                size="small"
              >
                <ToggleButton value="day">Jour</ToggleButton>
                <ToggleButton value="month">Mois</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Chip
                label={`${filteredInterventions.length} intervention(s)`}
                color="primary"
                size="small"
              />
            </Box>

            {filteredInterventions.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                Aucune intervention pour cette période
              </Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 500 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {viewMode === 'month' && <TableCell>Date</TableCell>}
                      <TableCell>Heure</TableCell>
                      <TableCell>Intervention</TableCell>
                      <TableCell>Demandeur</TableCell>
                      <TableCell>Lieu</TableCell>
                      <TableCell>Technicien</TableCell>
                      <TableCell>Durée</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredInterventions.map((intervention) => (
                      <TableRow key={intervention.id} hover>
                        {viewMode === 'month' && (
                          <TableCell>
                            <Chip
                              label={format(intervention.date, 'dd/MM', { locale: fr })}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AccessTimeIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {intervention.date ? format(intervention.date, 'HH:mm', { locale: fr }) : 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {intervention.title.replace(/^Demande #[A-Za-z0-9]+ - /, '')}
                          </Typography>
                          {intervention.description && (
                            <Typography variant="caption" color="text.secondary">
                              {intervention.description.substring(0, 30)}...
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PersonIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {intervention.requesterName || 'Non spécifié'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationOnIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {intervention.location}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PersonIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {intervention.technicianName || 'Non assigné'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${intervention.duration} min`}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default InterventionsTab;
